import { AxiosInstance } from 'axios'
import { Group, Team } from 'types/groups'
import {
  BatchMembersResponse,
  Member,
  MemberPageFilters,
  MemberRow,
  MembersFilter,
  OnboardingInvitePayload,
  OnboardingStats,
  UpdateMemberPayload
} from 'types/members'
import { ID, PaginatedResult } from 'types/utility'
import {
  cleanObject,
  cleanOnboardingParams,
  formatDateParams
} from 'utils/Functions'
import { MEMBER_ENDPOINTS } from 'api/ApiRoutes'
import { GetTransactionsParams, TransactionDetailed } from 'types/transactions'
import { GetTripsParams, TripDetailed } from 'types/trips'
import { Role } from 'types/misc'
import { Program } from 'types/programs'
import { BulkInviteResponse } from 'types/invitations'

class MembersApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getTeamMembers = async ({
    teamId,
    page,
    page_size,
    filters,
    is_manager = false,
    include_invites,
    q,
    filterByTeam = false
  }: {
    teamId: Team['token_id']
    page?: number | string
    page_size?: number | string
    filters?: MemberPageFilters
    is_manager?: boolean
    include_invites?: boolean
    q?: string
    filterByTeam?: boolean
  }) => {
    const queryObj = new URLSearchParams()
    if (filters) {
      Object.keys(filters).forEach((key) => {
        const filter = filters[key as keyof MemberPageFilters]
        if (filter?.id && !/all_/.test(`${filter?.id}`)) {
          // member_token_ids must be a comma-separated list
          queryObj.set(
            `${key}_token_id${key === 'member' ? 's' : ''}`,
            `${filter?.id}`
          )
        }
      })
    }

    if (is_manager) queryObj.set('is_manager', `${is_manager}`)
    if (include_invites !== undefined) {
      queryObj.set('include_invites', `${include_invites}`)
    }
    if (teamId && filterByTeam) queryObj.set('team_token_id', `${teamId}`)

    const queryString = queryObj.toString()
    const { data } = await this.axiosInstance.get<PaginatedResult<MemberRow>>(
      `${MEMBER_ENDPOINTS.MEMBERS(teamId)}?${queryString}`,
      {
        params: {
          q,
          page,
          page_size
        }
      }
    )
    return data
  }

  getMembers = async ({
    teamId,
    page,
    page_size,
    filters
  }: {
    teamId: Team['token_id']
    page?: number | string
    page_size?: number | string
    filters?: MembersFilter
  }) => {
    const { data } = await this.axiosInstance.get<PaginatedResult<MemberRow>>(
      MEMBER_ENDPOINTS.MEMBERS(teamId),
      {
        params: {
          ...cleanOnboardingParams(filters),
          page,
          page_size
        }
      }
    )
    return data
  }

  getTeamMemberTransactions = async ({
    team_id,
    member_id,
    ...params
  }: Partial<GetTransactionsParams> & {
    team_id: ID
    member_id: ID
  }) => {
    const { date_range, purpose, ...rest } = params ?? {}
    const apiParams = cleanObject({
      ...rest,
      purpose: purpose !== 'Unclassified' ? purpose : undefined,
      ...formatDateParams(date_range ?? [])
    })
    const { data } = await this.axiosInstance.get<TransactionDetailed[]>(
      MEMBER_ENDPOINTS.GET_MEMBER_TRANSACTIONS(team_id, member_id),
      {
        params: apiParams
      }
    )
    return data
  }

  getTeamMemberTrips = async ({
    team_id,
    member_id,
    ...params
  }: Partial<GetTripsParams> & {
    team_id: ID
    member_id: ID
  }) => {
    const { date_range, purpose, ...rest } = params ?? {}
    const apiParams = cleanObject({
      ...rest,
      purpose: purpose !== 'Unclassified' ? purpose : undefined,
      ...formatDateParams(date_range ?? [])
    })
    const { data } = await this.axiosInstance.get<TripDetailed[]>(
      MEMBER_ENDPOINTS.GET_MEMBER_TRIPS(team_id, member_id),
      {
        params: apiParams
      }
    )
    return data
  }

  getMembersCountPerRole = async ({
    teamId,
    roleId
  }: {
    teamId: Team['token_id']
    roleId: Role['token_id']
  }) => {
    const { data } = await this.axiosInstance.get<PaginatedResult<Member>>(
      MEMBER_ENDPOINTS.MEMBERS(teamId),
      {
        params: {
          role_token_id: roleId
        }
      }
    )
    return data
  }

  batchUpdateMembers = ({
    teamId,
    roles_to_add,
    roles_to_remove,
    new_team_token_id,
    member_token_ids,
    program_token_id,
    needs_program,
    mark_as_onboarded
  }: {
    teamId: ID
    member_token_ids: Member['token_id'][]
    roles_to_add?: Role['token_id'][]
    roles_to_remove?: Role['token_id'][]
    new_team_token_id?: Team['token_id']
    program_token_id?: Program['token_id'] | null
    needs_program?: boolean
    mark_as_onboarded?: boolean
  }) => {
    const body = {
      ...(roles_to_add?.length ? { roles_to_add: roles_to_add } : {}),
      ...(roles_to_remove?.length ? { roles_to_remove: roles_to_remove } : {}),
      ...(new_team_token_id ? { team_token_id: new_team_token_id } : {}),
      ...(program_token_id || program_token_id === null
        ? { program_token_id }
        : {}),
      ...(mark_as_onboarded ? { mark_as_onboarded } : {}),
      filter_params: {
        [needs_program ? 'needs_program' : 'member_token_ids']: needs_program
          ? true
          : member_token_ids
      }
    }

    return this.axiosInstance.put<BatchMembersResponse>(
      MEMBER_ENDPOINTS.MEMBERS_BATCH_UPDATE(teamId),
      body
    )
  }

  batchInviteMembers = async ({
    teamId,
    payload
  }: {
    teamId: ID
    payload: OnboardingInvitePayload
  }) => {
    const d = await this.axiosInstance.put<{
      token_id: string
    }>(MEMBER_ENDPOINTS.MEMBERS_BATCH_INVITE(teamId), payload)
    return d.data
  }

  deleteMemberInvite = async ({
    teamId,
    payload
  }: {
    teamId: ID
    payload: OnboardingInvitePayload
  }) => {
    const d = await this.axiosInstance.delete<{
      token_id: string
    }>(MEMBER_ENDPOINTS.MEMBERS_BATCH_INVITE(teamId), { data: payload })
    return d.data
  }

  getMember = async ({
    teamId,
    memberId
  }: {
    teamId: Team['token_id']
    memberId: Member['token_id']
  }) => {
    const { data } = await this.axiosInstance.get<Member>(
      MEMBER_ENDPOINTS.MEMBER(teamId, memberId)
    )
    return data
  }

  updateMember = async (
    teamId: Team['token_id'],
    memberId: Member['token_id'],
    payload: Partial<UpdateMemberPayload>
  ) => {
    const { data } = await this.axiosInstance.put(
      MEMBER_ENDPOINTS.MEMBER(teamId, memberId),
      payload
    )
    return data
  }

  uploadCsvBulkMemberUpdate = async ({
    file,
    teamId
  }: {
    file: File
    teamId: Group['token_id']
  }) => {
    const formData = new FormData()
    formData.append('bulk_member_update[data_file]', file)
    formData.append('bulk_member_update[root_team_token_id]', teamId as string)

    // Axios 1.x converts FormData to JSON when Content-Type is 'application/json' (set in defaultConfig).
    // Setting null removes that header so the browser sets multipart/form-data with the correct boundary.
    const d = await this.axiosInstance.post<BulkInviteResponse>(
      MEMBER_ENDPOINTS.MEMBERS_BULK_UPDATE(teamId),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return d.data
  }

  downloadBulkUpdateCSVTemplate = async ({
    teamId
  }: {
    teamId: Group['token_id']
  }) => {
    const d = await this.axiosInstance.get(
      MEMBER_ENDPOINTS.MEMBERS_BULK_UPDATE_TEMPLATE(teamId)
    )
    return d.data
  }

  getOnboardingStats = async ({
    teamId,
    archived,
    include_invites
  }: {
    teamId: Team['token_id']
    archived: boolean
    include_invites: boolean
  }) => {
    const { data } = await this.axiosInstance.get<OnboardingStats>(
      MEMBER_ENDPOINTS.MEMBERS_ONBOARDING_STATS(teamId),
      {
        params: { archived, include_invites }
      }
    )
    return data
  }
}

export default MembersApi
